import React, { useEffect, useMemo, useRef, useState } from "react";
import Modal from "react-bootstrap/Modal";

const RemarksForm = ({ domain, show, remarks, handleClose, handleFormSubmit }) => {
  const [newRemark, setNewRemark] = useState("");
  const inputRef = useRef(null);
  const listEndRef = useRef(null);

  useEffect(() => {
    if (show && inputRef.current) {
      setTimeout(() => inputRef.current?.focus(), 100);
    }
    if (!show) setNewRemark("");
  }, [show]);

  const remarkCount = remarks?.length || 0;

  const formatRelativeTime = (date) => {
    try {
      const diffMs = Date.now() - new Date(date);
      const sec = Math.floor(diffMs / 1000);
      const min = Math.floor(sec / 60);
      const hr = Math.floor(min / 60);
      const day = Math.floor(hr / 24);
      if (sec < 60) return `${sec}s ago`;
      if (min < 60) return `${min}m ago`;
      if (hr < 24) return `${hr}h ago`;
      return `${day}d ago`;
    } catch { return ""; }
  };

  const getDisplayName = (r) => {
    const u = r?.user;
    if (!u) return "Unknown";
    if (!u.anonymous && u.username) return u.username;
    const id = u._id || r.user;
    return id ? `Bleeper#${String(id).slice(-6)}` : "Bleeper";
  };

  const getInitials = (name) => {
    if (!name) return "U";
    const parts = String(name).trim().split(/\s+/);
    const first = parts[0]?.[0] || "";
    const last = parts.length > 1 ? parts[parts.length - 1]?.[0] : "";
    return (first + last || first).toUpperCase();
  };

  const canSubmit = newRemark.trim().length > 0;

  const submit = () => {
    if (!canSubmit) return;
    handleFormSubmit(newRemark.trim());
    setNewRemark("");
  };

  const onKeyDown = (e) => {
    if ((e.ctrlKey || e.metaKey) && e.key === "Enter") {
      e.preventDefault();
      submit();
    }
  };

  const sortedRemarks = useMemo(() => {
    if (!Array.isArray(remarks)) return [];
    return [...remarks].sort((a, b) => new Date(b.dateTime) - new Date(a.dateTime));
  }, [remarks]);

  const avatarColor = (name) => {
    const colors = ["#f59e0b", "#10b981", "#3b82f6", "#8b5cf6", "#ec4899", "#ff5100"];
    let hash = 0;
    for (const ch of String(name)) hash = ch.charCodeAt(0) + ((hash << 5) - hash);
    return colors[Math.abs(hash) % colors.length];
  };

  const overLimit = newRemark.length > 1000;

  return (
    <>
      <style>{`
        .remarks-modal .modal-content {
          background: #fff;
          border: 1px solid #e5e7eb;
          border-radius: 18px;
          box-shadow: 0 20px 60px rgba(0,0,0,0.12);
        }
        .remarks-modal .modal-header {
          background: #fff;
          border-bottom: 1px solid #f3f4f6;
          border-radius: 18px 18px 0 0;
          padding: 20px 24px 16px;
        }
        .remarks-modal .modal-body {
          background: #fff;
          padding: 20px 24px;
        }
        .remarks-modal .modal-footer {
          background: #f9fafb;
          border-top: 1px solid #f3f4f6;
          border-radius: 0 0 18px 18px;
          padding: 16px 24px;
        }
        .remark-card {
          display: flex;
          gap: 12px;
          align-items: flex-start;
          background: #f9fafb;
          border: 1px solid #f3f4f6;
          border-radius: 12px;
          padding: 12px 14px;
          margin-bottom: 10px;
          transition: border-color 0.12s;
        }
        .remark-card:hover { border-color: #e5e7eb; }
        .remark-textarea {
          width: 100%;
          min-height: 90px;
          resize: vertical;
          background: #fff;
          color: #111827;
          border: 1.5px solid #e5e7eb;
          border-radius: 10px;
          padding: 10px 14px;
          font-size: 14px;
          outline: none;
          transition: border-color 0.15s, box-shadow 0.15s;
        }
        .remark-textarea:focus {
          border-color: #ff5100;
          box-shadow: 0 0 0 3px rgba(255,81,0,0.1);
        }
      `}</style>

      <Modal
        show={show}
        onHide={handleClose}
        centered
        dialogClassName="remarks-modal"
        style={{ backdropFilter: "blur(3px)" }}
      >
        <Modal.Header closeButton>
          <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
            <div style={{
              width: 34, height: 34, borderRadius: 9,
              background: "rgba(255,81,0,0.08)", border: "1.5px solid rgba(255,81,0,0.2)",
              display: "flex", alignItems: "center", justifyContent: "center",
            }}>
              <i className="fas fa-comments" style={{ color: "#ff5100", fontSize: 13 }} />
            </div>
            <div>
              <div style={{ fontWeight: 800, fontSize: 16, color: "#111827" }}>Internal Remarks</div>
              <div style={{ fontSize: 12, color: "#9ca3af", marginTop: 1 }}>
                {remarkCount} remark{remarkCount !== 1 ? "s" : ""} on this ticket
              </div>
            </div>
          </div>
        </Modal.Header>

        <Modal.Body>
          {/* Remarks list */}
          {sortedRemarks.length > 0 ? (
            <div style={{ maxHeight: "44vh", overflowY: "auto", marginBottom: 16 }}>
              {sortedRemarks.map((remark) => {
                const name = getDisplayName(remark);
                const initials = getInitials(name);
                const role = remark?.user?.type && remark.user.type !== "user"
                  ? remark.user.type : null;
                const time = remark?.dateTime
                  ? new Date(remark.dateTime).toLocaleString() : "";
                const rel = remark?.dateTime
                  ? formatRelativeTime(remark.dateTime) : "";

                return (
                  <div key={remark._id || remark.dateTime} className="remark-card">
                    {/* Avatar */}
                    <div style={{
                      width: 36, height: 36, borderRadius: "50%",
                      background: avatarColor(name),
                      display: "flex", alignItems: "center", justifyContent: "center",
                      flexShrink: 0, color: "#fff", fontWeight: 800, fontSize: 12,
                      userSelect: "none",
                    }}>
                      {initials}
                    </div>

                    <div style={{ minWidth: 0, flex: 1 }}>
                      {/* Name + role + time */}
                      <div style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap", marginBottom: 4 }}>
                        <span style={{ fontWeight: 800, fontSize: 13, color: "#111827" }}>
                          @{name}
                        </span>
                        {role && (
                          <span style={{
                            display: "inline-flex", alignItems: "center", gap: 5,
                            padding: "2px 8px", borderRadius: 9999,
                            background: "#eef2ff", color: "#3730a3",
                            fontSize: 11, fontWeight: 700,
                          }}>
                            <i className="fas fa-shield-alt" style={{ fontSize: 10 }} /> {role}
                          </span>
                        )}
                        {rel && (
                          <span title={time} style={{ fontSize: 11, color: "#9ca3af" }}>
                            <i className="far fa-clock" style={{ marginRight: 3 }} />{rel}
                          </span>
                        )}
                      </div>

                      {/* Comment text */}
                      <div style={{
                        fontSize: 13, color: "#374151", lineHeight: 1.6,
                        whiteSpace: "pre-wrap", wordBreak: "break-word",
                      }}>
                        {remark.comment}
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          ) : (
            <div style={{
              border: "1px dashed #e5e7eb", borderRadius: 12,
              padding: "20px 16px", textAlign: "center",
              color: "#9ca3af", fontSize: 13, marginBottom: 16,
              background: "#fafafa",
            }}>
              <i className="far fa-sticky-note" style={{ fontSize: 22, display: "block", marginBottom: 6, opacity: 0.5 }} />
              No remarks yet. Be the first to add one.
            </div>
          )}

          {/* Divider before input */}
          <div style={{ height: 1, background: "#f3f4f6", marginBottom: 14 }} />

          {/* Input area */}
          <div>
            <textarea
              ref={inputRef}
              value={newRemark}
              onChange={(e) => setNewRemark(e.target.value)}
              onKeyDown={onKeyDown}
              placeholder="Write a new remark..."
              className="remark-textarea"
              aria-label="New remark"
              maxLength={1200}
            />
            <div style={{
              display: "flex", alignItems: "center", justifyContent: "space-between",
              marginTop: 6, fontSize: 11, color: "#9ca3af",
            }}>
              <span>
                Press{" "}
                <span style={{
                  fontFamily: "monospace", background: "#f3f4f6",
                  border: "1px solid #e5e7eb", borderRadius: 4,
                  padding: "0 5px", fontSize: 11, color: "#374151",
                }}>
                  Ctrl
                </span>
                {" "}+{" "}
                <span style={{
                  fontFamily: "monospace", background: "#f3f4f6",
                  border: "1px solid #e5e7eb", borderRadius: 4,
                  padding: "0 5px", fontSize: 11, color: "#374151",
                }}>
                  Enter
                </span>
                {" "}to post
              </span>
              <span style={{ color: overLimit ? "#dc2626" : "#9ca3af" }}>
                {newRemark.length}/1000
              </span>
            </div>
          </div>
        </Modal.Body>

        <Modal.Footer>
          <div style={{ display: "flex", gap: 10, width: "100%" }}>
            <button
              type="button"
              onClick={handleClose}
              style={{
                flex: 1, padding: "11px 0", borderRadius: 10,
                border: "1.5px solid #e5e7eb", background: "#fff",
                color: "#374151", fontWeight: 700, fontSize: 15,
                cursor: "pointer",
              }}
            >
              Close
            </button>
            <button
              type="button"
              onClick={submit}
              disabled={!canSubmit || overLimit}
              style={{
                flex: 2, padding: "11px 0", borderRadius: 10, border: "none",
                background: (!canSubmit || overLimit) ? "#e5e7eb" : "linear-gradient(135deg, #c13d00 0%, #ff5100 100%)",
                color: (!canSubmit || overLimit) ? "#9ca3af" : "#fff",
                fontWeight: 700, fontSize: 15,
                cursor: (!canSubmit || overLimit) ? "not-allowed" : "pointer",
                display: "flex", alignItems: "center", justifyContent: "center",
                gap: 9, transition: "background 0.15s",
              }}
            >
              Post Remark
              <i className="fas fa-arrow-right" style={{ fontSize: 13 }} />
            </button>
          </div>
        </Modal.Footer>
      </Modal>
    </>
  );
};

export default RemarksForm;
